/**
 * Customer-Portal API shared Zod schemas.
 *
 * Imported by:
 *   - `apps/api/src/routes/portal/*` — to validate response bodies + as
 *     the source of truth for the response schema in Fastify's
 *     `route.schema.response`.
 *   - `apps/customer-portal/src/lib/auth-context.tsx` etc. — to derive
 *     TS types for in-flight payloads and (optionally) to runtime-parse
 *     responses so a schema-drift between deploys surfaces immediately
 *     in the UI instead of silently rendering `undefined`.
 *
 * Schema change here touches both sides automatically. Convention:
 * wire-format keys stay `snake_case` (matches the wire); UI camelCases
 * via its own `fromWire(...)` helper.
 */
import { z } from 'zod';

/**
 * Portal customer-user role. Mirrors the `portal_user_role` enum in
 * `apps/api/src/db/schema/portal.ts` (OCP-110).
 */
export const portalUserRoleSchema = z.enum(['owner', 'admin', 'viewer', 'billing-only']);
export type PortalUserRole = z.infer<typeof portalUserRoleSchema>;

/**
 * `GET /v1/portal/auth/me` response, also embedded in
 * `POST /v1/portal/auth/login` (status=authenticated) and
 * `POST /v1/portal/auth/login/totp` responses. Single source of truth
 * for the customer-side identity payload.
 */
export const portalUserPayloadSchema = z.object({
  id: z.string().uuid(),
  customer_id: z.string().uuid(),
  email: z.string().email(),
  email_verified: z.boolean(),
  role: portalUserRoleSchema,
  totp_enabled: z.boolean(),
  /** OCP-656 - the dashboard language the user chose (null = browser default). */
  locale: z.enum(['de', 'en']).nullable(),
  /**
   * OCP-515 - a redeem code the customer carried in via /redeem?code=X and
   * brought through signup but has not consumed yet (null once redeemed, or
   * when the account never had a code in flight). The SPA reads this to
   * auto-continue to /redeem?code=X after a cross-device email-verify, where
   * the browser-local sessionStorage carry is empty. Customer-scoped: it is
   * the same value for every user of the account.
   */
  pending_redeem_code: z.string().nullable(),
});
export type PortalUserPayload = z.infer<typeof portalUserPayloadSchema>;

/**
 * `POST /v1/portal/auth/login` response discriminated union:
 *  - `authenticated` — session cookie was set, `user` is the identity
 *  - `totp_required` — caller has TOTP enrolled; client should hand the
 *    `login_token` to `POST /v1/portal/auth/login/totp`
 */
export const portalLoginResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('authenticated'), user: portalUserPayloadSchema }),
  z.object({ status: z.literal('totp_required'), login_token: z.string() }),
]);
export type PortalLoginResponse = z.infer<typeof portalLoginResponseSchema>;

/**
 * Stable error-body shape returned by every `/v1/portal/*` endpoint
 * when something goes wrong. UI surfaces specific copy by inspecting
 * `error` (snake_case_token).
 */
export const portalErrorResponseSchema = z.object({ error: z.string() });
export type PortalErrorResponse = z.infer<typeof portalErrorResponseSchema>;

/**
 * Generic "OK" response used by mutating endpoints with no payload
 * (logout, password-reset/request, email-verification/resend, …).
 */
export const portalOkResponseSchema = z.object({ ok: z.literal(true) });
export type PortalOkResponse = z.infer<typeof portalOkResponseSchema>;

// ---- Subscription / billing (Paddle integration, OCP-112) ----------

/**
 * Customer-facing subscription status mirror. Maps from Paddle's status
 * vocabulary onto a smaller UI-relevant set in
 * `apps/api/src/services/paddle-subscriptions.ts`. The UI shows
 * different actions per status (cancel, resume, retry-payment, …).
 */
export const portalSubscriptionStatusSchema = z.enum([
  'trialing',
  'active',
  'past_due',
  'paused',
  'cancelled',
  'expired',
]);
export type PortalSubscriptionStatus = z.infer<typeof portalSubscriptionStatusSchema>;

/**
 * Which provider bills a subscription, or `null` when none does (free
 * activations, code redemptions). Resolved server-side per row from the two
 * link columns - see `apps/api/src/services/subscription-provider.ts`.
 */
export const portalBillingProviderSchema = z
  .enum(['paddle', 'polar'])
  .nullable();
export type PortalBillingProvider = z.infer<typeof portalBillingProviderSchema>;

/**
 * Minimal subscription shape, kept in step with the wire format. The
 * AUTHORITATIVE response schema for `/v1/portal/subscriptions` lives in
 * `apps/api/src/routes/portal/subscriptions.ts` (it carries the product-join
 * fields too); this one exists for callers that only need the identity +
 * lifecycle bits.
 *
 * OCP-531: both link columns are nullable and either may be absent - a
 * subscription is billed by whichever one is set (`billing_provider` is the
 * resolved answer). The previous shape declared `paddle_subscription_id` as a
 * required string, which describes no row this product has issued since Polar
 * became the Merchant of Record.
 */
export const portalSubscriptionSchema = z.object({
  id: z.string().uuid(),
  paddle_subscription_id: z.string().nullable(),
  polar_subscription_id: z.string().nullable(),
  billing_provider: portalBillingProviderSchema,
  product_id: z.string(),
  status: portalSubscriptionStatusSchema,
  current_period_end: z.string(), // ISO-8601 datetime
  cancel_at_period_end: z.boolean(),
});
export type PortalSubscription = z.infer<typeof portalSubscriptionSchema>;
